import {
  AppBar,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { Close } from "@mui/icons-material";
import { WithHeadline, WithoutHeadline } from "./FormDialog";
import { useUILocale } from "../UILocale";

export type WithConfirmButton = {
  confirmButton: (
    buttonProps: Pick<ButtonProps, "variant" | "startIcon">,
  ) => React.ReactNode;
  confirmButtonText?: never;
  confirmButtonProps?: never;
  onConfirm?: never;
};
export type WithoutConfirmButton = {
  confirmButtonText: string;
  confirmButtonProps?: React.ComponentProps<typeof Button>;
  confirmButton?: never;
  onConfirm: () => void;
};

// TODO standardize naming conventions for confirm/submit and dismiss/cancel buttons
export type ConfirmDialogProps = Omit<
  DialogProps,
  | "onClose"
  | "aria-label"
  | "id"
  | "aria-labelledby"
  | "role"
  | "aria-describedby"
> &
  (WithHeadline | WithoutHeadline) &
  (WithConfirmButton | WithoutConfirmButton) & {
    id: string;
    useSingleAction?: boolean;
    dismissButtonText?: string;
    children: React.ReactNode;
    onClose?: () => void;
    onDismiss?: () => void;
  };

export function ConfirmDialog({
  id,
  useSingleAction,
  open,
  onClose,
  onConfirm,
  onDismiss,
  confirmButton,
  confirmButtonText,
  confirmButtonProps,
  dismissButtonText: dismissButtonTextOverride,
  headline,
  "aria-label": ariaLabel,
  ...props
}: ConfirmDialogProps) {
  // Adapts from a Full Screen Dialog on Mobile to a Basic Dialog on Desktop
  const { dialog } = useUILocale();
  const dismissButtonText = dismissButtonTextOverride || dialog.cancel;
  const fullScreen =
    props.fullScreen ??
    useMediaQuery("(max-width: 319px), (max-height: 319px)");

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
    if (onClose) {
      onClose();
    }
  }, [onClose, onDismiss]);

  const handleOnClose = useCallback(
    (_: any, reason: "backdropClick" | "escapeKeyDown") => {
      // Prevent closing on backdrop click to avoid accidental closes on form dialogs
      if (reason === "backdropClick") {
        return;
      }
      handleDismiss();
    },
    [handleDismiss],
  );

  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm();
    }
    if (onClose) {
      onClose();
    }
  }, [onConfirm, onClose]);

  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        cancelButtonRef.current?.focus();
      });
    }
  }, [open]);

  const hasHeadline = Boolean(headline); // handles null, undefined, and empty string
  const hasAriaLabel = Boolean(ariaLabel); // handles null, undefined, and empty string

  if (!hasHeadline && !hasAriaLabel) {
    throw new Error(
      "ConfirmDialog requires either a headline or an aria-label for accessibility",
    );
  }

  const labelId = hasHeadline ? `${id}-headline` : undefined;
  const descriptionId = `${id}-content`;

  // Applying the a11y props to Dialog directly doesn't work because Dialog doesn't forward them to the correct element (role="dialog")
  // so we need to apply them directly to the paper slotProp which does not have them as allowed props so we need to ignore the type error
  const a11yProps: React.HTMLAttributes<HTMLElement> = {
    "aria-describedby": descriptionId,
    role: "alertdialog",
    ...(hasHeadline
      ? {
          "aria-labelledby": labelId,
        }
      : {
          "aria-label": ariaLabel,
          "aria-labelledby": null,
        }),
  };

  const confirmButtonVariant = fullScreen ? "text" : "contained";
  const confirmButtonStartIcon = fullScreen
    ? {
        startIcon: undefined,
      }
    : {};
  const defaultConfirmButton = (
    <Button
      variant={confirmButtonVariant}
      color="primary"
      onClick={handleConfirm}
      {...confirmButtonProps}
    >
      {confirmButtonText}
    </Button>
  );
  const customConfirmButton = confirmButton
    ? confirmButton({
        variant: confirmButtonVariant,
        ...confirmButtonStartIcon,
      })
    : null;

  return (
    <Dialog
      open={open}
      maxWidth="xs" // can be overriden via props
      {...props}
      slotProps={{
        /** @ts-ignore */
        paper: { ...a11yProps },
      }}
      onClose={handleOnClose}
      fullScreen={fullScreen}
      fullWidth={true} // used in conjuntion with maxWidth when not fullScreen
    >
      {fullScreen && (
        <AppBar position="relative" elevation={0} color="default">
          <Toolbar>
            {!useSingleAction && (
              <IconButton
                onClick={handleDismiss}
                ref={cancelButtonRef}
                aria-label={dismissButtonText}
              >
                <Close />
              </IconButton>
            )}
            <Typography
              sx={{
                flexGrow: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {hasHeadline && <span id={labelId}>{headline}</span>}
            </Typography>
            {customConfirmButton || defaultConfirmButton}
          </Toolbar>
        </AppBar>
      )}
      {!fullScreen && hasHeadline && (
        <DialogTitle id={labelId}>{headline}</DialogTitle>
      )}
      <DialogContent id={descriptionId}>{props.children}</DialogContent>
      {!fullScreen && (
        <DialogActions>
          {!useSingleAction && (
            <Button ref={cancelButtonRef} onClick={handleDismiss}>
              {dismissButtonText}
            </Button>
          )}
          {customConfirmButton || defaultConfirmButton}
        </DialogActions>
      )}
    </Dialog>
  );
}
